import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PerinHealthGrid from '../../components/PerinHealthGrid';
import { setDataList, setSelectAll, setSelectedItem, setSelectedRows } from '../../components/PerinHealthGridSlice';
import { useNavigate } from 'react-router-dom';
import useAxios from '../../services/AxiosConfig';
import FilterManagementView from '../../components/FilterManagementView';
import { clearQuestion, resetAddQuestionnaire, setFieldValue, setIsUpdateMode, setExistingQuestionnaire } from './add-edit-questionnaire/AddQuestionnaireSlice';
import { resetFilterState } from '../../slicers/FilterSlice';
import { closeModal } from '../../components/PerinHealthModelSlice';
import managementStyles from '../ManagementPages.module.css';
import { showAlert } from '../../components/PerinHealthAlertSlice';
import { openModal } from '../../components/PerinHealthModelSlice';
import { formatDateString } from '../../utils/dateUtils';
import { setReloadGrid } from './QuestionnaireManagementSlicer';


const QuestionnaireManagement = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const axiosConfig = useSelector((state) => state.axiosConfig);
    const httpService = useAxios(axiosConfig.envURL);
    const perinHealthGrid = useSelector((state) => state.perinHealthGrid);
    const reloadGrid = useSelector((state) => state.questionnaireManagement.reloadGrid);
    const userId = useSelector((state) => state.userInfo.id);
    const userDateFormat = useSelector((state) => state.userInfo.dateFormat);
    const [activeQuestionnaireIds, setActiveQuestionnaireIds] = useState([]);

    const headers = [
        { key: 'id', label: "ID" },
        { key: 'name', label: "Name" },
        { key: 'type', label: "Type" },
        { key: 'groupName', label: "Group" },
        { key: 'date_created', label: 'Date Created' }
    ];

    useEffect(() => {
        if (reloadGrid) {
            getQuestionnaireById();
        }
    }, [reloadGrid]);

    useEffect(() => {
        getCarePrograms();
    }, []);

    const getQuestionnaireById = () => {
        dispatch(resetFilterState());
        httpService.get('/questionnaire/list/' + userId)
            .then((responseData) => {
                dispatch(setDataList([]));
                dispatch(setReloadGrid(false));
                if (Array.isArray(responseData.data.data)) {
                    const sortedDataList = responseData.data.data.sort((a, b) => new Date(a.receivedTime) - new Date(b.receivedTime));
                    const updatedDataList = sortedDataList.map((item, index) => {
                        return { 
                            ...item, 
                            id: index + 1,
                            date_created: formatDateString(item.receivedTime, userDateFormat)
                        };
                    }).filter(item => !item.deletedAt);

                    dispatch(setDataList(updatedDataList));
                    dispatch(setExistingQuestionnaire(updatedDataList[0]));
                } else {
                    console.error('Invalid data format:');
                }
            })
            .catch((err) => {
                console.log(err);
                dispatch(setReloadGrid(false));
            });
    };

    useEffect(() => {
        if (perinHealthGrid?.dataList && perinHealthGrid.dataList.length > 0) {
            const reformattedData = perinHealthGrid.dataList.map(item => {
                const newItem = {
                    ...item,
                    date_created: formatDateString(item.receivedTime, userDateFormat)
                };
                return newItem;
            });
            dispatch(setDataList(reformattedData));
        }
    }, [userDateFormat]);


    const getCarePrograms = () => {
        httpService.get(`careprogram/list/${userId}`)
        .then((responseData) => {
            if (Array.isArray(responseData.data.data)) {
                
                const uniqueQIds = new Set();
                for (const program of responseData.data.data) {
                    for (const q of program.questionnaires) {
                        uniqueQIds.add(q.questionnaireId);
                    }
                }
                setActiveQuestionnaireIds(Array.from(uniqueQIds));
            } else {
                setActiveQuestionnaireIds([]);
            }
        })
        .catch((err) => {
            console.log(err);
            setActiveQuestionnaireIds([]);
        });
    };

    const onRowClickHandler = (rowItem) => {
        dispatch(resetAddQuestionnaire());
        dispatch(setSelectedItem(rowItem));

        const updatedQuestions = rowItem.questions.map((question, questionIndex) => {
            const updatedPossibleAnswers = question.possibleAnswers.map((possibleAnswer, answerIndex) => ({
                ...possibleAnswer,
                answerId: answerIndex + 1,
                text: `Answer ${answerIndex + 1}`
            }));
            return {
                ...question,
                questionId: questionIndex + 1,
                possibleAnswers: updatedPossibleAnswers
            };
        });

        const updatedRowItem = {
            ...rowItem,
            previewQuestions: updatedQuestions,
            existingQuestionnaire: rowItem
        };
        dispatch(setFieldValue(updatedRowItem));
       // dispatch(addQuestionToPreview(updatedRowItem.questions));
        dispatch(clearQuestion());
        dispatch(setIsUpdateMode(true));
        navigate('/questionnaire-setup');
    };

    const showDeleteModal = () => {
        const qsToDelete = [];
        const qsCannotDelete = [];
        
        perinHealthGrid.selectedRows.forEach(q => {
            if (activeQuestionnaireIds.includes(q.questionnaireId)) {
                qsCannotDelete.push(q);
            } else {
                qsToDelete.push(q);
            }
        });

        const cannotDeleteNames = qsCannotDelete.map(q => q.name || q.id).join('\n');
        const canDeleteNames = qsToDelete.map(q => q.name || q.id).join('\n');

        // If all content cannot be deleted, show error alert and return early
        if (qsCannotDelete.length > 0 && qsToDelete.length === 0) {
            dispatch(
                openModal({
                    activeModalId: "FilterManagementView",
                    header: 'Cannot Delete Questionnaires',
                    message: `The following questionnaires cannot be deleted because they are assigned to a care program:\n\n<i>${cannotDeleteNames}</i>`,
                    buttons: [
                        { label: 'Ok', variant: 'primary', onClickKey: 'OK' },
                    ],
                })
            );
            return;
        } else if (qsCannotDelete.length > 0 && qsToDelete.length > 0) {
            dispatch(
                openModal({
                    activeModalId: "FilterManagementView",
                    header: 'Partial Delete Warning',
                    message: `The following questionnaires <strong>will</strong> be deleted:\n<i>${canDeleteNames}</i>\n\nThe following questionnaires <strong>cannot</strong> be deleted because they are currently assigned to care programs:\n<i>${cannotDeleteNames}</i>`,
                    buttons: [
                       
                        { label: 'Cancel', variant: 'secondary', onClickKey: 'NO' },
                        { label: 'Confirm', variant: 'primary', onClickKey: 'YES' },
                    ],
                })
            );
        } else {
            dispatch(
                openModal({
                    activeModalId: "FilterManagementView",
                    header: 'Delete Questionnaires?',
                    message: `The following questionnaires will be deleted:\n<i>${canDeleteNames}</i>`,
                    buttons: [
                       
                        { label: 'Cancel', variant: 'secondary', onClickKey: 'NO' },
                        { label: 'Confirm', variant: 'primary', onClickKey: 'YES' },
                    ],
                })
            );
        }
    };

    const onDeleteHandler = () => {
        dispatch(closeModal());

        const qsToDelete = [];
        
        perinHealthGrid.selectedRows.forEach(q => {
            if (!activeQuestionnaireIds.includes(q.questionnaireId)) {
                qsToDelete.push(q);
            }
        });

        const deletePromises = qsToDelete.map((selectedItem) => {
            const { groupId, questionnaireId } = selectedItem;
            return httpService.delete(`questionnaire/${groupId}?questionnaireId=${questionnaireId}`);
        });

        Promise.all(deletePromises)
            .then((responses) => {
                dispatch(setSelectedRows([]));
                dispatch(setSelectAll(false));
                getQuestionnaireById();
                dispatch(
                    showAlert({
                        header: "Questionnaire Deleted",
                        message: `Successfully deleted ${qsToDelete.length} questionnaire item(s)`,
                        type: 'success'
                    })
                );
            })
            .catch((errors) => {
                let errorMessage = "There was an error deleting questionnaires";
                if (errors?.response?.data?.message) {
                    errorMessage = errors?.response?.data?.message;
                }
                console.error('Error deleting rows:', errors);
                dispatch(
                    showAlert({
                        header: "Error",
                        message: errorMessage,
                        type: 'danger'
                    })
                );
            });
    }

    const onAddQuestionnaireHandler = () => {
        dispatch(resetAddQuestionnaire());
        dispatch(setExistingQuestionnaire(perinHealthGrid.dataList));
        navigate('/questionnaire-setup');
    }

    return (
        <div className={managementStyles.MiddleContent}>
            <FilterManagementView
                isOrganization={true}
                isGroup={true}
                addButtonLabel={'Add Questionnaire'}
                modelHeader = "Delete Questionnaire?"
                onAddHandler={onAddQuestionnaireHandler}
                onDeleteHandler={onDeleteHandler} 
                customDeleteModal={showDeleteModal}
            />
            <div className={managementStyles.GridContainer}>
                <PerinHealthGrid
                    dataList={perinHealthGrid.dataList}
                    headers={headers}
                    showCheckbox={true}
                    showEditIcon={true}
                    onRowClick={onRowClickHandler}
                    onDeleteRow={onDeleteHandler}
                    showHeaderFilter={true}
                    gridHeight="100%"
                />
            </div>
        </div>
    );
};

export default QuestionnaireManagement;
